import { Link, useParams } from 'react-router-dom'
import { ArrowRight } from 'lucide-react'
import { Button, Card, CardContent, Badge } from '@/components/ui'
import { formatPriceCents, getBrandBySlug, getBrandModel } from '@/data/brands'
import { useSeo } from '@/lib/seo'

export function BrandModelYearPage() {
  const { brand: brandSlug, model: modelSlug, year } = useParams<{
    brand: string
    model: string
    year: string
  }>()

  const brand = brandSlug ? getBrandBySlug(brandSlug) : undefined
  const model = brandSlug && modelSlug ? getBrandModel(brandSlug, modelSlug) : undefined
  const yearNum = year ? Number(year) : NaN

  if (brand && model && Number.isFinite(yearNum) && model.years.includes(yearNum)) {
    useSeo({
      title: `Used ${yearNum} ${brand.name} ${model.name} | Bespokian Auto`,
      description: model.seo?.description ?? model.description,
      image: `/brand-images/${brand.slug}.jpg`,
    })
  }

  if (!brand || !model || !Number.isFinite(yearNum) || !model.years.includes(yearNum)) {
    return (
      <div className="bg-gray-50 min-h-screen">
        <div className="container-page py-16">
          <Card className="max-w-xl mx-auto text-center">
            <CardContent className="py-10">
              <h1 className="text-2xl font-bold text-gray-900">Page not found</h1>
              <p className="mt-2 text-gray-600">
                We couldn’t find that brand/model/year page.
              </p>
              <Link to="/brands" className="inline-block mt-6">
                <Button>Browse Brands</Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="container-page py-8">
        <div className="mb-8">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Link to="/brands" className="hover:underline">
              Brands
            </Link>
            <span>/</span>
            <Link to={`/brands/${brand.slug}`} className="hover:underline">
              {brand.name}
            </Link>
            <span>/</span>
            <Link to={`/brands/${brand.slug}/${model.slug}`} className="hover:underline">
              {model.name}
            </Link>
            <span>/</span>
            <span className="font-medium text-gray-900">{yearNum}</span>
          </div>

          <h1 className="mt-2 text-3xl font-bold text-gray-900">
            {yearNum} {brand.name} {model.name}
          </h1>
          <p className="mt-2 text-gray-600 max-w-3xl">
            {model.description} This year page is a dedicated route with its own content shell,
            using the client-supplied model dataset for highlights/specs.
          </p>

          <div className="mt-4 flex flex-wrap gap-2">
            <span className="badge badge-primary">Brand page</span>
            <span className="badge bg-gray-100 text-gray-700">Model page</span>
            <Badge variant="success">Year {yearNum}</Badge>
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardContent>
                <h2 className="text-lg font-semibold text-gray-900">
                  About the {yearNum} {brand.name} {model.name}
                </h2>
                <p className="mt-2 text-gray-600">
                  {model.description}
                </p>

                <div className="mt-6 grid gap-4 sm:grid-cols-2">
                  <div className="rounded-lg border border-gray-200 p-4">
                    <h3 className="font-semibold text-gray-900">Key highlights</h3>
                    <ul className="mt-2 text-sm text-gray-600 space-y-1">
                      {model.highlights.map((h) => (
                        <li key={h}>{h}</li>
                      ))}
                    </ul>
                  </div>
                  <div className="rounded-lg border border-gray-200 p-4">
                    <h3 className="font-semibold text-gray-900">Key specs</h3>
                    <dl className="mt-2 text-sm text-gray-600 space-y-1">
                      {model.specs.slice(0, 5).map((s) => (
                        <div key={s.label} className="flex items-center justify-between gap-3">
                          <dt className="text-gray-500">{s.label}</dt>
                          <dd className="font-medium text-gray-900">{s.value}</dd>
                        </div>
                      ))}
                    </dl>
                  </div>
                </div>

                <div className="mt-6 rounded-lg bg-gray-50 p-4">
                  <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
                    <div>
                      <p className="text-sm text-gray-600">Typical starting price</p>
                      <p className="text-2xl font-bold text-gray-900">
                        {formatPriceCents(model.startingPrice)}
                      </p>
                    </div>
                    <div className="text-sm text-gray-600">
                      Inventory count (mock):{' '}
                      <span className="font-medium text-gray-900">{model.inventoryCount}</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-primary-50 border-primary-200">
              <CardContent className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <div>
                  <h3 className="font-semibold text-gray-900">
                    Ready to shop {brand.name}?
                  </h3>
                  <p className="mt-1 text-sm text-gray-600">
                    Browse inventory filtered by make, then refine by model and price.
                  </p>
                </div>
                <Link to={`/vehicles?make=${encodeURIComponent(brand.slug)}`}>
                  <Button>
                    Browse Inventory
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>

          <div className="space-y-6">
            <Card>
              <CardContent>
                <h3 className="font-semibold text-gray-900">More {model.name} pages</h3>
                <p className="mt-2 text-sm text-gray-600">
                  Jump back to the model to choose a different year.
                </p>
                <Link to={`/brands/${brand.slug}/${model.slug}`} className="inline-block mt-4">
                  <Button variant="outline">Back to model</Button>
                </Link>

                <div className="mt-4 flex flex-wrap gap-2">
                  {model.years.map((y) => (
                    <Link key={y} to={`/brands/${brand.slug}/${model.slug}/${y}`} className={y === yearNum ? "badge badge-primary" : "badge bg-gray-100 text-gray-700"}>
                      {y}
                    </Link>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <h3 className="font-semibold text-gray-900">Tools</h3>
                <div className="mt-3 space-y-2">
                  <Link to="/financing" className="link block">
                    Payment calculator
                  </Link>
                  <Link to="/trade-in" className="link block">
                    Trade-in estimator
                  </Link>
                  <Link to="/apply" className="link block">
                    Apply for financing
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  )
}

